import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { eliminarFotosDeProducto } from './fotosService';
import { registrarMovimiento } from './movimientosService';
import { registrarEvento } from './analyticsService';
import { redondearCantidad } from '../utils/unidades';

// Ofertas: aplica un % de descuento (precio_oferta) a varios productos.
export async function aplicarOferta(ids, pct) {
  const { data, error } = await supabase.rpc('aplicar_oferta', {
    p_negocio_id: getNegocioId(), p_ids: ids, p_pct: parseFloat(pct) || 0,
  });
  if (error) throw error;
  return data; // cantidad de productos afectados
}

// Quita la oferta (vuelve al precio original) de varios productos.
export async function quitarOferta(ids) {
  const { data, error } = await supabase.rpc('quitar_oferta', {
    p_negocio_id: getNegocioId(), p_ids: ids,
  });
  if (error) throw error;
  return data;
}

// 🛍️ Mostrar u ocultar del catálogo en línea varios productos de una vez
// (productos.activo). Va en tandas para no hacer URLs gigantes.
export async function cambiarCatalogoVarios(ids, mostrar) {
  let n = 0;
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('productos').update({ activo: mostrar })
      .eq('negocio_id', getNegocioId()).in('id', ids.slice(i, i + 150)).select('id');
    if (error) throw error;
    n += (data || []).length;
  }
  return n;
}

// La trazabilidad es best-effort: si falla el registro del movimiento,
// la operación principal ya ocurrió y no debe deshacerse desde aquí.
async function anotarMovimiento(datos) {
  try {
    await registrarMovimiento(datos);
  } catch (e) {
    console.error('No se pudo registrar el movimiento de inventario', e);
  }
}

// `publico`: el catálogo lee la vista productos_publicos (sin precio de costo
// y con el stock solo como disponible/agotado).
export async function obtenerProductos({ publico = false } = {}) {
  const { data, error } = await supabase
    .from(publico ? 'productos_publicos' : 'productos')
    .select('*')
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
  return data || [];
}

// Genera el siguiente código correlativo por categoría (ej. RI-0001, RI-0002).
// El prefijo viene de la tabla categorias.
export async function generarCodigoProducto(nombreCategoria) {
  const { data: cat, error: errorCat } = await supabase
    .from('categorias')
    .select('prefijo_codigo')
    .eq('negocio_id', getNegocioId())
    .eq('nombre', nombreCategoria)
    .maybeSingle();
  if (errorCat) throw errorCat;
  if (!cat) return 'SIN-CAT-' + Date.now();
  const prefijo = cat.prefijo_codigo;
  const { data, error } = await supabase
    .from('productos')
    .select('codigo')
    .eq('negocio_id', getNegocioId())
    .like('codigo', prefijo + '-%')
    .order('codigo', { ascending: false })
    .limit(1);
  if (error) throw error;
  let siguienteNumero = 1;
  if (data && data.length > 0) {
    const ultimoNumero = parseInt(data[0].codigo.split('-')[1], 10);
    if (!Number.isNaN(ultimoNumero)) siguienteNumero = ultimoNumero + 1;
  }
  return prefijo + '-' + String(siguienteNumero).padStart(4, '0');
}

export async function crearProducto(datos) {
  const codigo = await generarCodigoProducto(datos.categoria);
  const { data: creado, error } = await supabase
    .from('productos')
    .insert([{ ...datos, negocio_id: getNegocioId(), codigo, foto_principal: 1 }])
    .select()
    .single();
  if (error) throw error;
  await anotarMovimiento({
    producto: creado,
    tipo: 'creacion',
    cantidad: creado.stock || 0,
    stockAnterior: 0,
    stockNuevo: creado.stock || 0,
    motivo: 'Producto creado',
  });
  registrarEvento('producto_creado', { categoria: creado.categoria || null });
  return creado; // producto completo (incluye id y codigo)
}

// `productoAnterior` permite registrar el ajuste de stock cuando cambia.
// `motivo` (opcional) reemplaza el texto del movimiento, ej. 'Reposición de stock'.
export async function actualizarProducto(id, datos, productoAnterior = null, motivo = null) {
  const { error } = await supabase.from('productos').update(datos).eq('id', id);
  if (error) throw error;
  if (
    productoAnterior &&
    datos.stock !== undefined &&
    Number(datos.stock) !== Number(productoAnterior.stock || 0)
  ) {
    await anotarMovimiento({
      producto: { ...productoAnterior, id },
      tipo: 'ajuste',
      cantidad: redondearCantidad(datos.stock - Number(productoAnterior.stock || 0)),
      stockAnterior: Number(productoAnterior.stock || 0),
      stockNuevo: datos.stock,
      motivo: motivo || 'Ajuste manual desde edición',
    });
  }
}

// Elimina el producto y luego limpia sus fotos de Storage.
// Si la limpieza de fotos falla, el producto ya quedó eliminado:
// devolvemos la advertencia en lugar de fallar toda la operación.
export async function eliminarProducto(producto) {
  const { error } = await supabase.from('productos').delete().eq('id', producto.id);
  if (error) throw error;
  await anotarMovimiento({
    producto: { ...producto, id: null },
    tipo: 'eliminacion',
    cantidad: -(producto.stock || 0),
    stockAnterior: producto.stock || 0,
    stockNuevo: 0,
    motivo: 'Producto eliminado',
  });
  try {
    await eliminarFotosDeProducto(producto);
    return { fotosLimpiadas: true };
  } catch {
    return { fotosLimpiadas: false };
  }
}

export async function marcarFotoPrincipal(id, numeroFoto) {
  const { error } = await supabase
    .from('productos')
    .update({ foto_principal: numeroFoto })
    .eq('id', id);
  if (error) throw error;
}
